import {
  createSignal,
  createTimestampValue,
  TimestampValue,
  type TimestampValueOptions
} from "@zyzgroup/core-common";
import { beforeUnload$ } from "./dom";

// sessionStorage 存储会话数据
// sessionStorage 只能被当前标签页访问
// 即便是相同域名下的两个页面，只要它们 不在同一个浏览器窗口中 打开，那么它们的 Session Storage 内容也无法共享

// 为什么 sessionStorage 在不同 Tab 页不共享？
// 这是由 W3C 标准明确设计的,sessionStorage 的设计初衷就是为了“隔离”，而不是“共享”。
// 在 sessionStorage 的语境下，“Session（会话）”的定义范围被严格限制在当前标签页（Tab）或当前窗口（Window）中。
// LocalStorage 的范围： 同源（Same Origin） + 浏览器实例，只要域名一样，所有 Tab 共享数据
// SessionStorage 的范围： 同源 + 顶级浏览上下文（Top-level Browsing Context） , 即：一个 Tab 就是一个独立的 Session

// 设计目的：为了“多窗口并行操作”, 防止多窗口之间的数据干扰

// 举个场景例子：假设你在一个订票网站上，同时打开了两个 Tab：
// Tab A： 正在帮张三买去北京的票
// Tab B： 正在帮李四买去上海的票

// 如果 sessionStorage 是跨 Tab 共享的：
// 你在 Tab A 选择了“北京”，存入 Storage
// 你在 Tab B 选择了“上海”，数据被覆盖了
// 回到 Tab A 点击提交，结果系统读取的是最新的“上海”，导致张三买错票

// 因为有了 sessionStorage 的隔离特性：
// Tab A 和 Tab B 拥有完全独立的存储空间，互不干扰
// 这使得用户可以在同一个网站上同时进行多个独立的业务流程，而不用担心状态串台

// 关闭 浏览器 或 标签页, 清除 sessionStorage

// 刷新 标签页 或 恢复浏览器页面, 保留 sessionStorage

// 复制标签页（浏览器标签右键菜单中的复制标签页，非复制 URL）或者在脚本中使用 window.open() 打开同源页面 时会复制当前 sessionStorage 到新的标签页中
// 但是！ 这仅仅是拷贝（Copy） ，不是同步（Sync）
// 复制完成后，两个 Tab 的数据就断开了联系, 你在新 Tab 修改数据，旧 Tab 不会变；反之亦然。

export function createSessionStorage(
  key: string | symbol,
  options?: TimestampValueOptions
) {
  const k = typeof key == "string" ? key : key.toString();

  // read
  const getInDom = () => {
    try {
      const fromLocal = window.sessionStorage.getItem(k);
      if (fromLocal) {
        return JSON.parse(fromLocal);
      }
    } catch (e) {
      // do nothing
    }
    return undefined;
  };

  // write or remove
  const setInDom = (timestampValue: TimestampValue) => {
    try {
      if (typeof timestampValue.value != "undefined") {
        window.sessionStorage.setItem(k, JSON.stringify(timestampValue));
      } else {
        window.sessionStorage.removeItem(k);
      }
    } catch (e) {
      // do nothing
    }
  };

  return {
    get: () => createTimestampValue(getInDom(), options).get().value,
    set: (v: any) => setInDom(createTimestampValue(getInDom(), options).set(v))
  };
}

export function clearSessionStorage() {
  window.sessionStorage.clear();
}

// BroadcastChannel 专门用于同源下的跨上下文通信（Tab、Iframe、Worker）
// // Tab A
// const channel = createBroadcastChannel('app_channel');
// channel.postMessage('Hello Tab B');
// // Tab B
// const channel = createBroadcastChannel('app_channel');
// channel.subscribe((msg) => {
//   console.log(msg); // 收到 'Hello Tab B'
// });
export function createBroadcastChannel<T = any>(key: string) {
  const bc = new BroadcastChannel(key);

  const subscription = beforeUnload$().subscribe(() => {
    bc.close();
    subscription.unsubscribe();
  });

  const signal = createSignal<[T]>();

  // 接收数据
  bc.onmessage = (ev: MessageEvent<T>) => {
    signal.emit(ev.data);
  };

  return {
    postMessage: (action: any) => {
      bc.postMessage(action);
    },
    subscribe: signal.subscribe
  };
}
